import { useState, useCallback, useEffect } from 'react'
import { useDeleteDataSourceMutation, useTestDataSourceMutation } from '@/hooks/useDataSources'
import { useConnectionStore } from '@/stores/connectionStore'
import { useNavigate } from 'react-router-dom'
import type { DataSource } from '@/services/apiClient'
import {
  ServerIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { toast } from 'react-hot-toast'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'
import { SafetyLockDialog } from './SafetyLockDialog'
import { getAgentEncryptionSettings } from '@/utils/agentEncryption'

interface ConnectionCardProps {
  connection: DataSource
  onEdit: () => void
  isReorderMode?: boolean
}

// Removed unused TestConnectionResponse interface (response is normalized by service)

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null

export function ConnectionCard({ connection, onEdit, isReorderMode = false }: ConnectionCardProps) {
  const deleteConnectionMutation = useDeleteDataSourceMutation()
  const testConnectionMutation = useTestDataSourceMutation()
  const { setActiveConnection, activeConnection } = useConnectionStore()
  const navigate = useNavigate()
  const [lastTestResult, setLastTestResult] = useState<boolean | null>(
    connection.testStatus === 'success' ? true : 
    connection.testStatus === 'failed' ? false : null
  )
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSafetyLock, setShowSafetyLock] = useState(false)

  const isActive = activeConnection?.id === connection.id

  // Reset test result when this connection is no longer active
  useEffect(() => {
    if (!isActive && lastTestResult !== null) {
      setLastTestResult(null)
    }
  }, [isActive, lastTestResult])

  // Validate if agent datasource has required encryption key
  const validateAgentEncryption = useCallback((datasource: DataSource): string | null => {
    // Only validate if datasource has an agent
    if (!datasource.agentId) {
      return null
    }

    const encryptionSettings = getAgentEncryptionSettings(datasource.agentId)
    
    // Agent datasources require an encryption key to function properly
    // Check if key is missing or empty
    if (!encryptionSettings.key || encryptionSettings.key.trim().length === 0) {
      return `Agent datasource requires an encryption key to connect. Please configure the encryption key for this agent connection in the Agents page.`
    }

    return null
  }, [])

  // Actual connection logic without lock check
  const performConnection = useCallback(async () => {
    if (!connection.id) return
    
    // Validate agent encryption settings before attempting connection
    const encryptionError = validateAgentEncryption(connection)
    if (encryptionError) {
      setLastTestResult(false)
      toast.error(encryptionError)
      return
    }
    
    try {
      const result = await testConnectionMutation.mutateAsync({
        datasourceId: connection.id
      })
      const status = (isRecord(result) && typeof (result as { status?: unknown }).status === 'string') ? (result as { status?: string }).status : undefined
      const message = (isRecord(result) && typeof (result as { message?: unknown }).message === 'string') ? (result as { message?: string }).message : undefined
      const success = status === 'success'

      
      // If failed, try to understand the error better
      if (!success) {
        console.log(`🔍 [ConnectionCard] Connection failure details:`)
        console.log(`  - Status: ${status}`)
        console.log(`  - Message: ${message || 'No message provided'}`)
        console.log(`  - Full result object:`, JSON.stringify(result, null, 2))
      }
      setLastTestResult(success)
      
      if (success) {
        // Convert DataSource to DatabaseConnection format for the store
        const connectionData = {
          id: connection.id,
          name: connection.displayName || `${connection.host}:${connection.port}`,
          type: connection.databaseType === 'MySQL' ? 'mysql' as const : 'postgresql' as const,
          host: connection.host || '',
          port: connection.port || (connection.databaseType === 'MySQL' ? 3306 : 5432),
          username: connection.username || '',
          password: '', // We don't store passwords in the UI store
          database: connection.defaultDatabase || undefined,
          createdAt: connection.createdAt || new Date().toISOString(),
          updatedAt: connection.updatedAt || new Date().toISOString(),
        }

        setActiveConnection(connectionData)
        // Removed toast notification for connection
        
        // Navigate to explorer after successful connection
        const targetUrl = `/explorer/${connection.id}`
        
        try {
          navigate(targetUrl)
        } catch (error) {
          console.error(`🔍 [ConnectionCard] Error during navigation:`, error)
        }
        
      } else {
        console.log(`🔍 [ConnectionCard] Connection failed, showing error toast`)
        toast.error(`Failed to connect to ${connection.displayName}: ${message || 'Unknown error'}`)
      }
    } catch (error) {
      console.error(`🔍 [ConnectionCard] Connection test threw error:`, error)
      setLastTestResult(false)
      toast.error('Connection test failed')
    }
  }, [connection, validateAgentEncryption, testConnectionMutation, setActiveConnection, navigate])

  // Handle connection with lock check
  const handleConnect = useCallback(async () => {
    if (!connection.id) return
    
    // Check if datasource is locked and show safety dialog
    if (connection.isLocked) {
      setShowSafetyLock(true)
      return
    }
    
    // Proceed with connection
    await performConnection()
  }, [connection.id, connection.isLocked, performConnection])

  const handleSafetyLockConfirm = useCallback(() => {
    setShowSafetyLock(false)
    // Proceed with connection after safety verification
    void performConnection()
  }, [performConnection])

  const handleSafetyLockClose = useCallback(() => {
    setShowSafetyLock(false)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!connection.id) return

    try {
      await deleteConnectionMutation.mutateAsync(connection.id)
      toast.success('Connection deleted')
    } catch {
      toast.error('Failed to delete connection')
    }
  }, [connection.id, deleteConnectionMutation])

  const handleConnectClick = useCallback(() => {
    void handleConnect()
  }, [handleConnect])

  const handleCloseDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    void handleDeleteConfirm()
  }, [handleDeleteConfirm])

  const getStatusColor = () => {
    if (isActive) return 'text-green-600'
    if (lastTestResult === true) return 'text-green-600'
    if (lastTestResult === false) return 'text-red-600'
    return 'text-gray-400'
  }

  const getStatusIcon = () => {
    if (testConnectionMutation.isPending) {
      return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
    }
    if (isActive) return <CheckCircleIcon className="w-4 h-4" />
    if (lastTestResult === true) return <CheckCircleIcon className="w-4 h-4" />
    if (lastTestResult === false) return <XCircleIcon className="w-4 h-4" />
    return <div className="w-2 h-2 rounded-full bg-current" />
  }

  const getDatabaseTypeIcon = () => {
    switch (connection.databaseType) {
      case 'MySQL':
        return (
          <img 
            src="/images/logos/mysql-logo.avif" 
            alt="MySQL" 
            className="w-6 h-6"
          />
        )
      case 'PostgreSQL':
        return (
          <img 
            src="/images/logos/postgresql-logo.avif" 
            alt="PostgreSQL" 
            className="w-6 h-6"
          />
        )
      case undefined:
        return <ServerIcon className="w-6 h-6 text-gray-500" />
      default:
        return <ServerIcon className="w-6 h-6 text-gray-500" />
    }
  }

  return (
    <div className={clsx(
      "bg-white dark:bg-gray-800 rounded-lg shadow-sm border transition-all duration-200 p-6 hover:shadow-md flex flex-col min-h-0 relative overflow-visible",
      isActive 
        ? "border-green-500 dark:border-green-400 ring-1 ring-green-500/20" 
        : "border-gray-200 dark:border-gray-700",
      isReorderMode && "select-none"
    )}>
      <div className="flex items-start justify-between min-w-0 gap-4">
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              {getDatabaseTypeIcon()}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="group relative">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate flex items-center">
                {connection.displayName}
                {connection.isLocked && (
                  <LockClosedIcon 
                    className="w-4 h-4 ml-2 text-amber-500 flex-shrink-0" 
                    title="This connection is locked for safety"
                  />
                )}
              </h3>
              {connection.displayName && connection.displayName.length > 10 && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-sm rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999] min-w-max">
                  {connection.displayName}
                </div>
              )}
            </div>
            <div className="mt-1 space-y-1">
              <div className="group relative">
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  <span className="inline-flex items-center">
                    <ServerIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span className="truncate">{connection.host}:{connection.port}</span>
                  </span>
                </p>
                {connection.host && `${connection.host}:${connection.port}`.length > 25 && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999] min-w-max">
                    {connection.host}:{connection.port}
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                <span className="truncate">{connection.databaseType} • {connection.username}</span>
              </p>
              {connection.defaultDatabase && (
                <div className="group relative">
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                    <span className="truncate">Default: {connection.defaultDatabase}</span>
                  </p>
                  {connection.defaultDatabase.length > 15 && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999] min-w-max">
                      Default: {connection.defaultDatabase}
                    </div>
                  )}
                </div>
              )}
              {/* TODO: When backend supports agents in datasources, display agent info here
              {connection.agentId && (
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  <span className="inline-flex items-center">
                    <CpuChipIcon className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span className="truncate">Agent: {connection.agent?.name}</span>
                  </span>
                </p>
              )}
              */}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {(isActive || lastTestResult === false) && (
            <div className={clsx('flex items-center space-x-1', getStatusColor())}>
              {getStatusIcon()}
              <span className="text-xs font-medium whitespace-nowrap">
                {isActive ? 'Active' : 'Failed'}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end gap-4">
        {!isReorderMode && (
          <div className="flex items-center space-x-2 flex-shrink-0">
            <button type="button"
              onClick={handleConnectClick}
              disabled={testConnectionMutation.isPending}
              className={clsx(
                "flex items-center space-x-1 text-xs px-3 py-1 whitespace-nowrap",
                isActive ? "btn-success" : "btn-secondary"
              )}
            >
              <PlayIcon className="w-3 h-3 flex-shrink-0" />
              <span>{isActive ? 'Active' : lastTestResult === true ? 'Connect' : 'Connect'}</span>
            </button>
            <button type="button"
              onClick={onEdit}
              className="btn-secondary flex items-center space-x-1 text-xs px-3 py-1 whitespace-nowrap"
            >
              <PencilIcon className="w-3 h-3 flex-shrink-0" />
              <span>Edit</span>
            </button>
            <button type="button"
              onClick={() => { setShowDeleteConfirm(true) }}
              disabled={deleteConnectionMutation.isPending}
              className="btn-danger flex items-center space-x-1 text-xs px-3 py-1 whitespace-nowrap"
            >
              <TrashIcon className="w-3 h-3 flex-shrink-0" />
              <span>Delete</span>
            </button>
          </div>
        )}
      </div>
      
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title="Delete Connection"
        message={`Are you sure you want to delete the connection "${connection.displayName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isDangerous={true}
      />
      
      {connection.isLocked && (
        <SafetyLockDialog
          isOpen={showSafetyLock}
          onClose={handleSafetyLockClose}
          onConfirm={handleSafetyLockConfirm}
          datasourceName={connection.displayName || 'Unknown'}
        />
      )}
    </div>
  )
}
